import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, subMonths } from 'date-fns';
import {
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  HelpCircle,
  Mail,
  Pencil,
  Phone,
  Receipt,
  ScrollText,
  Search,
  Settings2,
  Trash2,
  User,
  Building2,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../api';
import Layout from '../../components/Layout';
import SlideOver from '../../components/ui/SlideOver';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import {
  ClientForm,
  ContactForm,
  getClientDisplayName,
  isClientInvoiceReminderEmailsEnabled,
} from './clientsForms';

const fmtMoney = (n) =>
  `£${(typeof n === 'number' ? n : parseFloat(n || 0)).toFixed(2)}`;

const shortAccountRef = (id) => {
  if (!id || typeof id !== 'string') return '—';
  const compact = id.replace(/-/g, '');
  return compact.slice(0, 8).toUpperCase();
};

const invoiceStatusLabel = (inv) => {
  const s = inv.status || '';
  if (s === 'paid-in-full') return 'PAID IN FULL';
  if (s === 'part-paid') return 'PART PAID';
  if (s === 'sent') return 'SENT';
  if (s === 'draft') return 'DRAFT';
  if (s === 'overdue') return 'OVERDUE';
  return s.replace(/-/g, ' ').toUpperCase() || '—';
};

const statusBadgeClass = (status) => {
  if (status === 'paid-in-full')
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300';
  if (status === 'part-paid')
    return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200';
  if (status === 'sent')
    return 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300';
  if (status === 'overdue')
    return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  if (status === 'draft')
    return 'bg-[var(--ds-control-field-bg)] text-[var(--ds-text-secondary)]';
  return 'bg-[var(--ds-control-field-bg)] text-[var(--ds-text-muted)]';
};

function buildMonthBuckets(rangeEnd, count = 3) {
  const buckets = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = subMonths(rangeEnd, i);
    buckets.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: format(d, 'MMM yyyy'),
      start: new Date(d.getFullYear(), d.getMonth(), 1),
    });
  }
  return buckets;
}

function monthKeyFromInvoice(inv) {
  const raw = inv.dateIssued || inv.dateCreated || inv.createdAt;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const DetailRow = ({ label, children }) => (
  <div className="py-2 border-b border-[var(--ds-border-divider)] last:border-0">
    <dt className="text-micro font-medium text-[var(--ds-text-muted)] uppercase tracking-wide">
      {label}
    </dt>
    <dd className="mt-0.5 text-xs text-[var(--ds-text-primary)]">{children}</dd>
  </div>
);

const SectionCard = ({ title, headerRight, children, className = '' }) => (
  <div
    className={`bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-lg)] shadow-[var(--ds-shadow-card)] overflow-hidden ${className}`}
  >
    <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-[var(--ds-border-divider)] bg-[var(--ds-control-field-bg)]">
      <h2 className="text-xs font-semibold text-[var(--ds-text-primary)]">{title}</h2>
      {headerRight}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activityFilter, setActivityFilter] = useState('all');
  const [rangeEnd, setRangeEnd] = useState(() => new Date());
  const [showEdit, setShowEdit] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const createRef = useRef(null);
  const viewRef = useRef(null);

  const [contacts, setContacts] = useState([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [invoiceReminderSaving, setInvoiceReminderSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const [clientRes, activeRes, archivedRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get('/invoices', { params: { client_id: id, limit: 300, view: 'active' } }),
        api.get('/invoices', { params: { client_id: id, limit: 300, view: 'archived' } }),
      ]);

      const c = clientRes.data;
      setClient(c);
      setContacts(c.contacts || []);

      const map = new Map();
      for (const inv of activeRes.data.invoices || []) map.set(inv.id, inv);
      for (const inv of archivedRes.data.invoices || []) map.set(inv.id, inv);
      setInvoices([...map.values()].sort((a, b) => {
        const ta = new Date(a.createdAt || a.dateCreated || 0).getTime();
        const tb = new Date(b.createdAt || b.dateCreated || 0).getTime();
        return tb - ta;
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load client');
      setClient(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onDoc = (e) => {
      if (createRef.current && !createRef.current.contains(e.target)) setCreateOpen(false);
      if (viewRef.current && !viewRef.current.contains(e.target)) setViewOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const refreshContacts = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get(`/clients/${id}/contacts`);
      setContacts(res.data.contacts || []);
    } catch {
      setContacts([]);
    }
  }, [id]);

  const handleDeleteContact = async (contactId) => {
    if (!confirm('Are you sure you want to delete this contact?')) return;
    try {
      await api.delete(`/clients/${id}/contacts/${contactId}`);
      refreshContacts();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete contact');
    }
  };

  const handleInvoiceReminderToggle = async (nextEnabled) => {
    if (!id) return;
    setInvoiceReminderSaving(true);
    setError('');
    try {
      await api.put(`/clients/${id}`, {
        preferences: { invoiceReminderEmailsEnabled: nextEnabled },
      });
      setClient((c) =>
        c
          ? {
              ...c,
              preferences: {
                ...(c.preferences && typeof c.preferences === 'object' ? c.preferences : {}),
                invoiceReminderEmailsEnabled: nextEnabled,
              },
            }
          : c
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update reminder preference');
    } finally {
      setInvoiceReminderSaving(false);
    }
  };

  const monthBuckets = useMemo(() => buildMonthBuckets(rangeEnd, 3), [rangeEnd]);

  const monthlyTable = useMemo(() => {
    const rows = monthBuckets.map((b) => ({
      ...b,
      invoiced: 0,
      received: 0,
    }));
    const byKey = Object.fromEntries(rows.map((r) => [r.key, r]));

    for (const inv of invoices) {
      const mk = monthKeyFromInvoice(inv);
      if (mk && byKey[mk]) {
        byKey[mk].invoiced += parseFloat(inv.total) || 0;
        byKey[mk].received += parseFloat(inv.amountPaid) || 0;
      }
    }

    return rows.map((r) => ({
      ...r,
      balance: r.invoiced - r.received,
    }));
  }, [invoices, monthBuckets]);

  const chartData = useMemo(
    () => monthlyTable.map((r) => ({ name: r.label, amount: r.invoiced })),
    [monthlyTable]
  );

  const invoiceOutstanding = useMemo(
    () => invoices.reduce((s, inv) => s + (parseFloat(inv.balanceDue) || 0), 0),
    [invoices]
  );

  const prepayments = 0;

  const activityRows = useMemo(() => {
    let list = [...invoices];
    if (activityFilter === 'outstanding') {
      list = list.filter((inv) => (parseFloat(inv.balanceDue) || 0) > 0.005);
    }
    list.sort((a, b) => {
      const da = new Date(a.dateIssued || a.dateCreated || a.createdAt || 0).getTime();
      const db = new Date(b.dateIssued || b.dateCreated || b.createdAt || 0).getTime();
      return db - da;
    });
    return list.slice(0, 25);
  }, [invoices, activityFilter]);

  const addressLine = useMemo(() => {
    if (!client) return '';
    const parts = [
      client.address_line1,
      client.address_line2,
      [client.city, client.county].filter(Boolean).join(', '),
      client.postcode,
    ].filter(Boolean);
    return parts.join(', ');
  }, [client]);

  const mapsUrl = addressLine
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine)}`
    : null;

  if (loading && !client) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-16 text-center text-[var(--ds-text-muted)] text-xs">
          Loading client…
        </div>
      </Layout>
    );
  }

  if (error && !client) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] text-xs mb-4">
            {error}
          </div>
          <Button variant="secondary" onClick={() => navigate('/tenant/clients')}>
            ← Back to Clients
          </Button>
        </div>
      </Layout>
    );
  }

  const displayName = getClientDisplayName(client);

  return (
    <Layout>
      <div className="border-b border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/tenant/clients')} className="gap-1">
              <ArrowLeft className="h-4 w-4" />
              Clients
            </Button>
            <div className="hidden sm:block h-4 w-px bg-[var(--ds-border-divider)] mx-1" />
            <div className="relative" ref={createRef}>
              <Button variant="secondary" size="sm" onClick={() => setCreateOpen((o) => !o)} className="gap-1">
                + Create new
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Button>
              {createOpen && (
                <div className="absolute left-0 mt-1 z-20 min-w-[11rem] rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] shadow-lg py-1">
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)] flex items-center gap-2"
                    onClick={() => {
                      setCreateOpen(false);
                      navigate('/tenant/invoices/new');
                    }}
                  >
                    <FileText className="h-3.5 w-3.5" />
                    Invoice
                  </button>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)] flex items-center gap-2"
                    onClick={() => {
                      setCreateOpen(false);
                      navigate('/tenant/estimates/new');
                    }}
                  >
                    <Receipt className="h-3.5 w-3.5" />
                    Estimate
                  </button>
                </div>
              )}
            </div>
            <div className="relative" ref={viewRef}>
              <Button variant="secondary" size="sm" onClick={() => setViewOpen((o) => !o)} className="gap-1">
                View
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Button>
              {viewOpen && (
                <div className="absolute left-0 mt-1 z-20 min-w-[12rem] rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] shadow-lg py-1">
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                    onClick={() => {
                      setViewOpen(false);
                      navigate('/tenant/invoices');
                    }}
                  >
                    All invoices
                  </button>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-xs text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]"
                    onClick={() => {
                      setViewOpen(false);
                      navigate('/tenant/estimates');
                    }}
                  >
                    All estimates
                  </button>
                </div>
              )}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/tenant/clients/${id}/statement`)}
              className="gap-1"
            >
              <ScrollText className="h-3.5 w-3.5" />
              View Statement
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowEdit(true)} className="gap-1">
              <Pencil className="h-3.5 w-3.5" />
              Modify client
            </Button>
          </div>
          <h1 className="mt-3 text-display font-bold text-[var(--ds-text-primary)]">{displayName}</h1>
          <p className="text-meta text-[var(--ds-text-secondary)] mt-0.5">
            Client record · Ref {shortAccountRef(client?.id)}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] text-xs mb-4">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column */}
          <div className="space-y-6 lg:col-span-1">
            <SectionCard title="Details">
              <dl>
                <DetailRow label="Account ref">{shortAccountRef(client.id)}</DetailRow>
                <DetailRow label="Business / name">{displayName}</DetailRow>
                <DetailRow label="Type">
                  <span className="inline-flex items-center gap-1">
                    {client.client_type === 'company' ? (
                      <Building2 className="h-3.5 w-3.5" />
                    ) : (
                      <User className="h-3.5 w-3.5" />
                    )}
                    {client.client_type === 'company' ? 'Company' : 'Individual'}
                  </span>
                </DetailRow>
                {(client.address_line1 || client.city || client.postcode) && (
                  <DetailRow label="Address">
                    <div className="space-y-1">
                      {client.address_line1 && <div>{client.address_line1}</div>}
                      {client.address_line2 && <div>{client.address_line2}</div>}
                      {(client.city || client.county) && (
                        <div>
                          {[client.city, client.county].filter(Boolean).join(', ')}
                        </div>
                      )}
                      {client.postcode && <div>{client.postcode}</div>}
                      {mapsUrl && (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[var(--ds-accent-primary)] hover:underline mt-1"
                        >
                          View on map
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </DetailRow>
                )}
                <DetailRow label="Country">
                  <span className="inline-flex items-center gap-1.5">
                    {(client.country || 'GB').toUpperCase() === 'GB' && <span aria-hidden>🇬🇧</span>}
                    {client.country === 'GB' || !client.country
                      ? 'United Kingdom'
                      : client.country}
                  </span>
                </DetailRow>
                <DetailRow label="Default terms">
                  {client.payment_terms_days != null ? `${client.payment_terms_days} days` : '7 days'}
                </DetailRow>
                <DetailRow label="Average time to pay">
                  <span className="inline-flex items-center gap-2">
                    <span className="text-[var(--ds-text-muted)]">Not tracked</span>
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-[var(--ds-control-field-bg)] text-[var(--ds-text-muted)]"
                      title="Coming soon: calculated from paid invoices"
                    >
                      —
                    </span>
                  </span>
                </DetailRow>
                <DetailRow label="Status">
                  <Badge variant={client.is_active ? 'success' : 'neutral'} className="text-[11px]">
                    {client.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </DetailRow>
              </dl>
            </SectionCard>

            <SectionCard title="Email automation">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="detail-invoice-reminder-emails"
                  className="mt-0.5 h-4 w-4 shrink-0 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-focus-ring)] border-gray-300 dark:border-gray-600 rounded cursor-pointer disabled:opacity-50"
                  checked={isClientInvoiceReminderEmailsEnabled(client)}
                  disabled={invoiceReminderSaving}
                  onChange={(e) => handleInvoiceReminderToggle(e.target.checked)}
                />
                <div className="min-w-0">
                  <label
                    htmlFor="detail-invoice-reminder-emails"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    Automated invoice reminders
                  </label>
                  <p className="text-micro text-[var(--ds-text-muted)] mt-1">
                    When enabled, this client can receive automated reminder emails for sent or unpaid
                    invoices (per your tenant settings). Turn off to opt this client out.
                  </p>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title="Contact details"
              headerRight={
                <button
                  type="button"
                  onClick={() => setShowAddContact(true)}
                  className="text-[11px] font-medium text-[var(--ds-accent-primary)] hover:underline"
                >
                  + Add contact
                </button>
              }
            >
              <div className="space-y-2">
                <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-amber-50/80 dark:bg-amber-950/25 p-3">
                  <div className="text-[11px] font-semibold text-amber-900 dark:text-amber-200 mb-1">
                    Primary
                  </div>
                  <div className="text-xs font-medium text-[var(--ds-text-primary)]">{displayName}</div>
                  {client.email && (
                    <a
                      href={`mailto:${client.email}`}
                      className="text-xs text-[var(--ds-accent-primary)] flex items-center gap-1 mt-1"
                    >
                      <Mail className="h-3 w-3 shrink-0" />
                      {client.email}
                    </a>
                  )}
                  {(client.phone || client.mobile) && (
                    <div className="text-xs text-[var(--ds-text-secondary)] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {client.phone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {client.phone}
                        </span>
                      )}
                      {client.mobile && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {client.mobile}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] p-3 bg-[var(--ds-surface-panel)]"
                  >
                    <div className="flex justify-between gap-2">
                      <div>
                        <div className="text-xs font-medium text-[var(--ds-text-primary)]">
                          {contact.first_name} {contact.last_name}
                        </div>
                        {contact.position && (
                          <div className="text-[11px] text-[var(--ds-text-muted)]">{contact.position}</div>
                        )}
                        {contact.email && (
                          <a
                            href={`mailto:${contact.email}`}
                            className="text-xs text-[var(--ds-accent-primary)] flex items-center gap-1 mt-1"
                          >
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </a>
                        )}
                        {(contact.phone || contact.mobile) && (
                          <div className="text-xs text-[var(--ds-text-secondary)] mt-1 space-y-0.5">
                            {contact.phone && <div>{contact.phone}</div>}
                            {contact.mobile && <div>{contact.mobile} (mobile)</div>}
                          </div>
                        )}
                      </div>
                      <div className="flex items-start gap-0.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => setEditingContact(contact)}
                          className="p-1.5 text-[var(--ds-text-muted)] hover:text-[var(--ds-accent-primary)]"
                          title="Edit"
                        >
                          <Settings2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteContact(contact.id)}
                          className="p-1.5 text-[var(--ds-text-muted)] hover:text-[var(--ds-status-danger)]"
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {client.notes && (
              <SectionCard title="Notes">
                <p className="text-xs text-[var(--ds-text-secondary)] whitespace-pre-wrap">{client.notes}</p>
              </SectionCard>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-6 lg:col-span-2">
            <SectionCard title="Account balance at a glance">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] p-3">
                  <div className="text-micro text-[var(--ds-text-muted)] uppercase tracking-wide">
                    Invoice balance
                  </div>
                  <div className="text-lg font-semibold text-[var(--ds-text-primary)] mt-1">
                    {fmtMoney(invoiceOutstanding)}
                  </div>
                  <div className="text-[10px] text-[var(--ds-text-muted)] mt-0.5">GBP · outstanding</div>
                </div>
                <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] p-3">
                  <div className="text-micro text-[var(--ds-text-muted)] uppercase tracking-wide">
                    Prepayments
                  </div>
                  <div className="text-lg font-semibold text-[var(--ds-text-primary)] mt-1">
                    {fmtMoney(prepayments)}
                  </div>
                  <div className="text-[10px] text-[var(--ds-text-muted)] mt-0.5">GBP</div>
                </div>
                <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] p-3 bg-[var(--ds-control-field-bg)]">
                  <div className="text-micro text-[var(--ds-text-muted)] uppercase tracking-wide">Total</div>
                  <div
                    className={`text-lg font-semibold mt-1 ${
                      parseFloat(client.current_balance || 0) < 0
                        ? 'text-[var(--ds-status-danger)]'
                        : 'text-[var(--ds-text-primary)]'
                    }`}
                  >
                    {fmtMoney(client.current_balance)}
                  </div>
                  <div className="text-[10px] text-[var(--ds-text-muted)] mt-0.5">Stored account balance</div>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title="Sales by month"
              headerRight={
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-[var(--ds-surface-panel)] text-[var(--ds-text-muted)]"
                    onClick={() => setRangeEnd((d) => subMonths(d, 3))}
                    aria-label="Previous quarter"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-[11px] text-[var(--ds-text-secondary)] whitespace-nowrap px-1">
                    {monthBuckets[0]?.label} – {monthBuckets[2]?.label}
                  </span>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-[var(--ds-surface-panel)] text-[var(--ds-text-muted)]"
                    onClick={() => setRangeEnd((d) => {
                      const n = new Date(d);
                      n.setMonth(n.getMonth() + 3);
                      return n;
                    })}
                    aria-label="Next quarter"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              }
            >
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-left text-[var(--ds-text-muted)] border-b border-[var(--ds-border-divider)]">
                      <th className="py-2 pr-4 font-medium">Period</th>
                      <th className="py-2 pr-4 font-medium text-right">Total invoiced</th>
                      <th className="py-2 pr-4 font-medium text-right">Total received</th>
                      <th className="py-2 font-medium text-right">Monthly balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyTable.map((row) => (
                      <tr key={row.key} className="border-b border-[var(--ds-border-divider)]">
                        <td className="py-2 pr-4 text-[var(--ds-text-primary)]">{row.label}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{fmtMoney(row.invoiced)}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{fmtMoney(row.received)}</td>
                        <td className="py-2 text-right tabular-nums font-medium">{fmtMoney(row.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 h-56 w-full">
                <p className="text-[11px] font-medium text-[var(--ds-text-secondary)] mb-2">Monthly sales</p>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-[var(--ds-border-subtle)]" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--ds-text-muted)' }} />
                    <YAxis
                      tick={{ fontSize: 10, fill: 'var(--ds-text-muted)' }}
                      tickFormatter={(v) => `£${v}`}
                    />
                    <RechartsTooltip
                      formatter={(value) => [fmtMoney(value), 'Invoiced']}
                      contentStyle={{
                        background: 'var(--ds-surface-panel)',
                        border: '1px solid var(--ds-border-subtle)',
                        borderRadius: 'var(--ds-radius-md)',
                        fontSize: 12,
                      }}
                    />
                    <Bar
                      dataKey="amount"
                      fill="var(--ds-accent-primary)"
                      radius={[4, 4, 0, 0]}
                      name="Invoiced"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>

            <SectionCard
              title="Recent activity"
              headerRight={
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-[var(--ds-text-muted)]">Invoices:</span>
                  <button
                    type="button"
                    className={
                      activityFilter === 'all'
                        ? 'font-semibold text-[var(--ds-accent-primary)]'
                        : 'text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)]'
                    }
                    onClick={() => setActivityFilter('all')}
                  >
                    all
                  </button>
                  <span className="text-[var(--ds-border-divider)]">|</span>
                  <button
                    type="button"
                    className={
                      activityFilter === 'outstanding'
                        ? 'font-semibold text-[var(--ds-accent-primary)]'
                        : 'text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)]'
                    }
                    onClick={() => setActivityFilter('outstanding')}
                  >
                    outstanding
                  </button>
                </div>
              }
            >
              {activityRows.length === 0 ? (
                <p className="text-xs text-[var(--ds-text-muted)]">No invoices to show.</p>
              ) : (
                <ul className="space-y-2">
                  {activityRows.map((inv) => (
                    <li
                      key={inv.id}
                      className="flex flex-wrap items-center gap-2 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] p-3 hover:bg-[var(--ds-control-field-bg)]/50"
                    >
                      <FileText className="h-4 w-4 text-[var(--ds-text-muted)] shrink-0" />
                      <button
                        type="button"
                        onClick={() => navigate(`/tenant/invoices/${inv.id}`)}
                        className="text-xs font-medium text-[var(--ds-accent-primary)] hover:underline text-left"
                      >
                        #{inv.invoiceNum}
                        {inv.dateIssued && (
                          <span className="text-[var(--ds-text-muted)] font-normal">
                            {' '}
                            · {format(new Date(inv.dateIssued), 'd MMM yyyy')}
                          </span>
                        )}
                      </button>
                      <span className="text-[var(--ds-text-muted)]" title="Invoice details">
                        <HelpCircle className="h-3.5 w-3.5" />
                      </span>
                      <span className="text-xs font-medium tabular-nums ml-auto sm:ml-0">
                        {fmtMoney(inv.total)}
                      </span>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusBadgeClass(inv.status)}`}
                      >
                        {invoiceStatusLabel(inv)}
                      </span>
                      <button
                        type="button"
                        onClick={() => navigate(`/tenant/invoices/${inv.id}`)}
                        className="p-1.5 text-[var(--ds-text-muted)] hover:text-[var(--ds-accent-primary)]"
                        title="Open invoice"
                      >
                        <Search className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>
        </div>
      </div>

      <SlideOver
        open={showEdit}
        onOpenChange={setShowEdit}
        title={displayName ? `Edit ${displayName}` : 'Edit client'}
      >
        {showEdit && client && (
          <ClientForm
            client={client}
            onClose={() => setShowEdit(false)}
            onSuccess={() => {
              setShowEdit(false);
              load();
            }}
          />
        )}
      </SlideOver>

      {(showAddContact || editingContact) && (
        <ContactForm
          clientId={id}
          contact={editingContact}
          onClose={() => {
            setShowAddContact(false);
            setEditingContact(null);
          }}
          onSuccess={() => {
            setShowAddContact(false);
            setEditingContact(null);
            refreshContacts();
          }}
        />
      )}
    </Layout>
  );
}
